import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is authenticated on app start
  useEffect(() => {
    const checkAuth = async () => {
      const token = api.getAuthToken();
      const cachedUser = localStorage.getItem('user_data');
      console.log('[AuthContext] Checking auth on mount. Token exists:', !!token);

      if (token) {
        console.log('[AuthContext] Token found in localStorage, validating with server...');

        // First, load cached user data if available (for immediate UI update)
        if (cachedUser) {
          try {
            const parsedUser = JSON.parse(cachedUser);
            console.log('[AuthContext] Loading cached user data:', parsedUser.email);
            setUser(parsedUser);
            setIsAuthenticated(true);
          } catch (e) {
            console.error('[AuthContext] Failed to parse cached user data:', e);
          }
        }

        // Then validate token with server
        try {
          const profileData = await api.getProfile();
          console.log('[AuthContext] Token validation SUCCESS - user profile fetched:', profileData.email);
          setUser(profileData);
          setIsAuthenticated(true);
          // Cache the user data for future page loads
          localStorage.setItem('user_data', JSON.stringify(profileData));
        } catch (error) {
          console.error('[AuthContext] Token validation FAILED:', error);
          console.error('[AuthContext] Error type:', error.constructor.name);
          console.error('[AuthContext] Error message:', error.message);
          console.error('[AuthContext] Response status:', error.response?.status);
          console.error('[AuthContext] Response data:', error.response?.data);

          // Only clear auth on 401/403 (invalid token)
          // Keep user logged in on network errors if we have cached data
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.log('[AuthContext] Token is invalid (401/403) - logging out');
            api.clearAuthToken();
            localStorage.removeItem('user_data');
            setUser(null);
            setIsAuthenticated(false);
          } else if (cachedUser) {
            // Network error but we have cached data - stay logged in
            console.log('[AuthContext] Network error but cached user data exists - keeping user logged in');
            setIsAuthenticated(true);
          } else {
            // Network error and no cached data - log out
            console.log('[AuthContext] Network error and no cached data - logging out');
            api.clearAuthToken();
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } else {
        console.log('[AuthContext] No token found in localStorage - user not authenticated');
        setUser(null);
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await api.login(credentials);
      const { token, user: userData } = response;

      // Store token and user data
      api.setAuthToken(token);
      localStorage.setItem('user_data', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Login failed. Please try again.'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.register(userData);
      return { success: true, data: response };
    } catch (error) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || error.response?.data?.message || 'Registration failed. Please try again.'
      };
    }
  };

  const logout = () => {
    api.clearAuthToken();
    localStorage.removeItem('user_data');
    setUser(null);
    setIsAuthenticated(false);
  };

  const refreshProfile = async () => {
    try {
      // Fetch the current user profile data
      const profileData = await api.getProfile();
      setUser(profileData);
      localStorage.setItem('user_data', JSON.stringify(profileData));
      return profileData;
    } catch (error) {
      console.error('Failed to refresh profile:', error);
      // If profile refresh fails, keep current user state
      return user;
    }
  };

  // Check if user has admin role
  const isAdmin = user?.roles?.some(role => role.name === 'admin') || false;

  const value = {
    user,
    isAuthenticated,
    isAdmin,
    isLoading,
    login,
    register,
    logout,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};